const MODIFIER_ORDER: ReadonlyArray<string> = ['Mod', 'Meta', 'Ctrl', 'Alt', 'Shift'];

const MODIFIER_LOOKUP: ReadonlySet<string> = new Set([
	'Mod',
	'Meta',
	'Cmd',
	'Command',
	'Ctrl',
	'Control',
	'Alt',
	'Option',
	'Shift',
]);

export function isMacPlatform(): boolean {
	if (typeof navigator === 'undefined') {
		return false;
	}

	const platform = (navigator.platform ?? '').toLowerCase();
	const userAgent = (navigator.userAgent ?? '').toLowerCase();
	return platform.includes('mac') || userAgent.includes('mac');
}

function canonicalizeKeyToken(rawToken: string): string {
	const token = rawToken.trim();

	if (token.length === 0) {
		return '';
	}

	const lower = token.toLowerCase();

	if (lower === 'mod') {
		return 'Mod';
	}

	if (lower === 'cmd' || lower === 'command' || lower === 'meta') {
		return 'Meta';
	}

	if (lower === 'ctrl' || lower === 'control') {
		return 'Ctrl';
	}

	if (lower === 'alt' || lower === 'option' || lower === 'opt') {
		return 'Alt';
	}

	if (lower === 'shift') {
		return 'Shift';
	}

	if (lower === 'escape' || lower === 'esc') {
		return 'Escape';
	}

	if (lower === 'space' || lower === 'spacebar' || lower === ' ') {
		return 'Space';
	}

	if (lower === 'enter' || lower === 'return') {
		return 'Enter';
	}

	if (lower === 'backspace' || lower === 'delete') {
		return lower === 'delete' ? 'Delete' : 'Backspace';
	}

	if (lower === 'up' || lower === 'arrowup') {
		return 'ArrowUp';
	}

	if (lower === 'down' || lower === 'arrowdown') {
		return 'ArrowDown';
	}

	if (lower === 'left' || lower === 'arrowleft') {
		return 'ArrowLeft';
	}

	if (lower === 'right' || lower === 'arrowright') {
		return 'ArrowRight';
	}

	if (token.length === 1) {
		return token.toUpperCase();
	}

	if (/^f[0-9]{1,2}$/i.test(token)) {
		return token.toUpperCase();
	}

	return token.length > 1 ? token.charAt(0).toUpperCase() + token.slice(1).toLowerCase() : token.toUpperCase();
}

function normalizeComboSegment(segment: string): string {
	const rawTokens = segment
		.split('+')
		.map((token) => token.trim())
		.filter((token) => token.length > 0);

	const canonical = rawTokens.map(canonicalizeKeyToken).filter((token) => token.length > 0);

	const modifiers = [...new Set(canonical.filter((token) => MODIFIER_LOOKUP.has(token)))];
	const nonModifiers = canonical.filter((token) => !MODIFIER_LOOKUP.has(token));

	modifiers.sort((left, right) => MODIFIER_ORDER.indexOf(left) - MODIFIER_ORDER.indexOf(right));

	return [...modifiers, ...nonModifiers].join('+');
}

export function normalizeKeys(rawKeys: string): string {
	if (rawKeys.length === 0) {
		return '';
	}

	return rawKeys
		.split(/\s+/)
		.map((segment) => normalizeComboSegment(segment))
		.filter((segment) => segment.length > 0)
		.join(' ');
}

export function migrateLegacyModifier(keys: string, isMac: boolean = isMacPlatform()): string {
	if (keys.length === 0) {
		return '';
	}

	const migrated = keys
		.split(' ')
		.map((segment) =>
			segment
				.split('+')
				.map((token) => {
					if (token === 'Meta') {
						return 'Mod';
					}

					if (token === 'Ctrl' && !isMac) {
						return 'Mod';
					}

					return token;
				})
				.join('+')
		)
		.join(' ');

	return normalizeKeys(migrated);
}

export function keysFromKeyboardEvent(event: KeyboardEvent, isMac: boolean = isMacPlatform()): string {
	const tokens: string[] = [];

	if (event.metaKey) {
		tokens.push(isMac ? 'Mod' : 'Meta');
	}

	if (event.ctrlKey) {
		tokens.push(isMac ? 'Ctrl' : 'Mod');
	}

	if (event.altKey) {
		tokens.push('Alt');
	}

	if (event.shiftKey) {
		tokens.push('Shift');
	}

	tokens.sort((left, right) => MODIFIER_ORDER.indexOf(left) - MODIFIER_ORDER.indexOf(right));

	const key = event.key;

	if (!MODIFIER_LOOKUP.has(canonicalizeKeyToken(key))) {
		tokens.push(canonicalizeKeyToken(key));
	}

	return tokens.join('+');
}

export function formatKeysForDisplay(keys: string): string {
	if (keys.length === 0) {
		return '';
	}

	const mac = isMacPlatform();

	return keys
		.split(' ')
		.map((segment) => {
			return segment
				.split('+')
				.map((token) => {
					if (!mac) {
						if (token === 'Meta' || token === 'Mod') {
							return 'Ctrl';
						}

						return token;
					}

					switch (token) {
						case 'Mod':
							return '⌘';
						case 'Meta':
							return '⌘';
						case 'Ctrl':
							return '⌃';
						case 'Alt':
							return '⌥';
						case 'Shift':
							return '⇧';
						case 'Enter':
							return '↵';
						case 'Escape':
							return '⎋';
						case 'Backspace':
							return '⌫';
						case 'Delete':
							return '⌦';
						case 'ArrowUp':
							return '↑';
						case 'ArrowDown':
							return '↓';
						case 'ArrowLeft':
							return '←';
						case 'ArrowRight':
							return '→';
						default:
							return token;
					}
				})
				.join(mac ? '' : '+');
		})
		.join(' ');
}

/**
 * true, wenn irgendein Segment ausschließlich aus Modifiern besteht —
 * solche "Kürzel" würden bei jedem Halten der Modifier feuern und sind nicht speicherbar.
 */
export function isModifierOnlyKeys(keys: string): boolean {
	if (keys.length === 0) {
		return false;
	}

	return keys.split(' ').some((segment) => segment.split('+').every((token) => MODIFIER_LOOKUP.has(token)));
}

export function isSingleLetterKeys(keys: string): boolean {
	if (keys.length === 0 || keys.includes(' ')) {
		return false;
	}

	const tokens = keys.split('+');

	if (tokens.length !== 1) {
		return false;
	}

	const token = tokens[0] ?? '';

	return token.length === 1 && /^[A-Z0-9]$/.test(token);
}
